import { useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router';

import { ROUTES_MAP } from 'app/shared/utils/routes';
import {
  U21Chip,
  U21ChipProps,
  U21NoValue,
} from 'app/shared/u21-ui/components';

import {
  DataSettingFieldType,
  FormattedDataItem,
  OrgDataSettingsConfig,
} from 'app/modules/dataSettings/responses';
import { useEntityDetails } from 'app/modules/entities/queries/useEntityDetails';
import { useInstrumentDetails } from 'app/modules/instruments/queries/useInstrumentDetails';
import {
  selectHasReadEntitiesPermissions,
  selectHasReadInstrumentsPermission,
} from 'app/modules/session/selectors';
import {
  useToggleEntitySidebar,
  useToggleInstrumentSidebar,
} from 'app/modules/sidebar/hooks';

interface PerspectiveChipProps extends Omit<
  U21ChipProps,
  'ref' | 'as' | 'children'
> {
  formattedDataItem: FormattedDataItem;
  dataSetting?: OrgDataSettingsConfig;
}

export const PerspectiveChip = (props: PerspectiveChipProps) => {
  const { formattedDataItem, dataSetting, ...rest } = props;

  // Page location id
  const { id: pageId } = useParams<{ id?: string }>();

  // Entity page
  const isOnEntityPage = useRouteMatch(ROUTES_MAP.entitiesId.path);
  const { data: entityExternalId } = useEntityDetails(
    pageId || '',
    (data) => data.external_id,
    false,
  );

  // Instrument page
  const isOnInstrumentPage = useRouteMatch(ROUTES_MAP.instrumentsId.path);
  const { data: instrumentExternalId } = useInstrumentDetails(
    pageId || '',
    (data) => data.external_id,
    false,
  );

  // permissions
  const canReadEntityPermissions = useSelector(
    selectHasReadEntitiesPermissions,
  );
  const canReadInstrumentPermissions = useSelector(
    selectHasReadInstrumentsPermission,
  );

  const toggleEntitySidebar = useToggleEntitySidebar();
  const toggleInstrumentSidebar = useToggleInstrumentSidebar();

  if (!dataSetting || !formattedDataItem?.formatted_value) {
    return <U21NoValue />;
  }

  switch (dataSetting.data_type) {
    case DataSettingFieldType.ENTITY_REFERENCE:
      return (
        <U21Chip
          onClick={
            // can be an external entity, in which case no id
            formattedDataItem.formatted_value.unit21_id &&
            canReadEntityPermissions
              ? (e) => {
                  e.stopPropagation();
                  toggleEntitySidebar(
                    String(formattedDataItem.formatted_value.unit21_id),
                    e,
                  );
                }
              : undefined
          }
          color={
            isOnEntityPage &&
            entityExternalId === formattedDataItem.formatted_value.external_id
              ? 'primary'
              : undefined
          }
          {...rest}
        >
          {(() => {
            if (!formattedDataItem.formatted_value.external_id) {
              return formattedDataItem.formatted_value.unit21_id;
            }
            if (
              !dataSetting.rendering_options?.reference_display_fields?.length
            ) {
              return formattedDataItem.formatted_value.name_readable;
            }
            const fields =
              dataSetting.rendering_options.reference_display_fields;
            return fields
              .map((field) => formattedDataItem.formatted_value[field])
              .join(' — ');
          })()}
        </U21Chip>
      );
    case DataSettingFieldType.INSTRUMENT_REFERENCE:
      return (
        <U21Chip
          onClick={
            canReadInstrumentPermissions
              ? (e) => {
                  e.stopPropagation();
                  toggleInstrumentSidebar(
                    String(formattedDataItem.formatted_value.unit21_id),
                    e,
                  );
                }
              : undefined
          }
          color={
            isOnInstrumentPage &&
            instrumentExternalId ===
              formattedDataItem.formatted_value.external_id
              ? 'primary'
              : undefined
          }
          {...rest}
        >
          {formattedDataItem.formatted_value.external_id ||
            formattedDataItem.formatted_value.unit21_id}
        </U21Chip>
      );
    default:
      return null;
  }
};
